import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  TextField,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import {
  getEmployeeInfo,
  getEmployeeLocations,
  assignEmployeeLocation,
  removeEmployeeLocation,
} from "../../../../utils/services/control/masterDataService";
import {
  deactivateUser,
  reactivateUser,
  unlockAccount,
  adminResetPassword,
  adminForceLogout,
} from "../../../../utils/services/control/userManagementService";
import ConfirmDialog from "../common/ConfirmDialog";
import MfaManageDialog from "./MfaManageDialog";

const InfoRow = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </Grid>
);

const EmployeeDetailDialog = ({
  open,
  onClose,
  employee,
  allLocations = [],
  onUserChanged,
}) => {
  const [info, setInfo] = useState(null);
  const [empLocations, setEmpLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addLocationId, setAddLocationId] = useState("");
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [userAction, setUserAction] = useState(null);
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [resetPwdValue, setResetPwdValue] = useState("");
  const [mfaOpen, setMfaOpen] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadData = useCallback(async () => {
    if (!employee) return;
    setLoading(true);
    try {
      const [infoData, locsData] = await Promise.all([
        getEmployeeInfo(employee.employeeId),
        getEmployeeLocations(employee.employeeId),
      ]);
      // El backend retorna una fila por rol — guardamos la lista entera
      // para mostrar varios roles, pero info[0] hace de header común.
      setInfo(Array.isArray(infoData) ? infoData : [infoData]);
      setEmpLocations(Array.isArray(locsData) ? locsData : []);
    } catch (err) {
      console.error("Error loading employee detail:", err);
    } finally {
      setLoading(false);
    }
  }, [employee]);

  useEffect(() => {
    if (open && employee) {
      loadData();
      setError("");
      setSuccess("");
      setAddLocationId("");
    }
  }, [open, employee, loadData]);

  const empName = `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim();
  const header = info?.[0];

  // Roles activos del empleado (el SP devuelve una fila por rol; si no
  // tiene roles, devuelve una fila con roleId=null).
  const rolesList = (info || [])
    .filter((row) => row?.roleId)
    .map((row) => ({
      roleId: row.roleId,
      roleName: row.roleName,
      roleKey: row.roleKey,
    }));

  const handleAssignLocation = async () => {
    if (!addLocationId) return;
    setSaving(true);
    setError("");
    try {
      // Si el empleado aún no tiene locations, la primera se marca primary
      // para mantener consistencia con AuthContext.primaryLocation.
      const isFirstLocation = empLocations.length === 0;
      const res = await assignEmployeeLocation({
        employeeId: employee.employeeId,
        locationId: Number(addLocationId),
        primary: isFirstLocation,
      });
      if (res.result !== 0) setError(res.message);
      else {
        setAddLocationId("");
        loadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  // Promover una location secundaria a primary.
  // El SP no demota automáticamente al primary anterior y no deja al
  // empleado sin primary, así que primero promovemos la nueva (dos
  // primaries temporalmente) y después demotamos la antigua.
  const handleSetPrimary = async (locationId) => {
    setSaving(true);
    setError("");
    try {
      const previousPrimary = empLocations.find(
        (l) => l.isPrimary && l.locationId !== locationId
      );
      const promote = await assignEmployeeLocation({
        employeeId: employee.employeeId,
        locationId,
        primary: true,
      });
      if (promote.result !== 0) {
        setError(promote.message);
        return;
      }
      if (previousPrimary) {
        const demote = await assignEmployeeLocation({
          employeeId: employee.employeeId,
          locationId: previousPrimary.locationId,
          primary: false,
        });
        if (demote.result !== 0) setError(demote.message);
      }
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLocation = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await removeEmployeeLocation({
        employeeId: employee.employeeId,
        locationId: removeConfirm.locationId,
      });
      if (res.result !== 0) setError(res.message);
      else loadData();
    } catch (err) {
      setError(err.response?.data?.message || "Error");
    } finally {
      setSaving(false);
      setRemoveConfirm(null);
    }
  };

  const handleUserAction = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      let res;
      switch (userAction) {
        case "deactivate":
          res = await deactivateUser(employee.employeeId);
          break;
        case "reactivate":
          res = await reactivateUser(employee.employeeId);
          break;
        case "unlock":
          res = await unlockAccount(employee.employeeId);
          break;
        case "forceLogout":
          res = await adminForceLogout({
            targetEmployeeId: employee.employeeId,
            reason: "Admin forced logout from employee detail",
          });
          break;
        default:
          return;
      }
      if (res.result !== 0) setError(res.message);
      else {
        setSuccess(res.message || "Action completed successfully");
        onUserChanged?.();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error");
    } finally {
      setSaving(false);
      setUserAction(null);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwdValue.trim()) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await adminResetPassword({
        employeeId: employee.employeeId,
        tempPassword: resetPwdValue,
      });
      if (res.result !== 0) setError(res.message);
      else {
        setSuccess("Password reset successfully");
        setResetPwdOpen(false);
        setResetPwdValue("");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  const availableLocations = allLocations.filter(
    (l) =>
      l.status === 1 &&
      !empLocations.some((el) => el.locationId === l.locationId)
  );

  const userActionMessages = {
    deactivate: `Deactivate user account for ${empName}? Their active session will be closed.`,
    reactivate: `Reactivate user account for ${empName}? They will need to change their password.`,
    unlock: `Unlock account for ${empName}?`,
    forceLogout: `Force logout for ${empName}? Their current session will be terminated.`,
  };

  const userActionTitles = {
    deactivate: "Deactivate User",
    reactivate: "Reactivate User",
    unlock: "Unlock Account",
    forceLogout: "Force Logout",
  };

  const userActionLabels = {
    deactivate: "Deactivate",
    reactivate: "Reactivate",
    unlock: "Unlock",
    forceLogout: "Force Logout",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Employee Detail — {empName}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              General Information
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <InfoRow label="Employee ID" value={employee?.employeeId} />
              <InfoRow
                label="Full Name"
                value={`${header?.firstName || employee?.firstName} ${header?.lastName || employee?.lastName}`}
              />
              <InfoRow
                label="Personal Email"
                value={header?.mail || employee?.mail}
              />
              <InfoRow
                label="Business Email"
                value={header?.businessEmail || employee?.businessEmail}
              />
              <InfoRow
                label="Job Position"
                value={header?.jobPositionName || employee?.jobPositionName}
              />
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Roles
                </Typography>
                {rolesList.length === 0 ? (
                  <Typography variant="body2">No role assigned</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                    {rolesList.map((r) => (
                      <Chip
                        key={r.roleId}
                        label={r.roleName}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              User Account
            </Typography>
            {employee?.hasUser ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, flexWrap: "wrap", alignItems: "center" }}
              >
                <Chip label="Has account" size="small" color="success" />
                <Tooltip title="Deactivate User">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setUserAction("deactivate")}
                  >
                    <PersonOffOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Unlock Account">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => setUserAction("unlock")}
                  >
                    <LockOpenOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Password">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setResetPwdOpen(true);
                      setResetPwdValue("");
                    }}
                  >
                    <LockResetOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Force Logout">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setUserAction("forceLogout")}
                  >
                    <LogoutOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Manage 2FA">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setMfaOpen(true)}
                  >
                    <ShieldOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No user account. Use the Create User button in the employees table.
              </Typography>
            )}

            {resetPwdOpen && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  mb: 2,
                  p: 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <TextField
                  label="New Temporary Password"
                  value={resetPwdValue}
                  onChange={(e) => setResetPwdValue(e.target.value)}
                  size="small"
                  type="password"
                  sx={{ flex: 1 }}
                  helperText="User will be required to change this on next login."
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleResetPassword}
                  disabled={saving || !resetPwdValue.trim()}
                  sx={{ mt: 0.5 }}
                >
                  Reset
                </Button>
                <Button
                  size="small"
                  onClick={() => setResetPwdOpen(false)}
                  disabled={saving}
                  sx={{ mt: 0.5 }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Assigned Locations ({empLocations.length})
            </Typography>

            <TableContainer sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell align="center">Primary</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Orden alfabético — el SP devuelve primary-first, pero
                      eso hace que al cambiar el primary la fila "salte"
                      arriba y el admin no nota el cambio. */}
                  {[...empLocations]
                    .sort((a, b) =>
                      (a.medicalCenter || "").localeCompare(
                        b.medicalCenter || "",
                        undefined,
                        { sensitivity: "base" }
                      )
                    )
                    .map((loc) => (
                      <TableRow key={loc.locationId}>
                        <TableCell>{loc.medicalCenter}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {loc.addressOffice || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {loc.isPrimary ? (
                            <Tooltip title="This is the primary location">
                              <StarIcon fontSize="small" color="warning" />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Set as primary location">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleSetPrimary(loc.locationId)
                                  }
                                  disabled={saving}
                                >
                                  <StarBorderIcon
                                    fontSize="small"
                                    sx={{ color: "text.secondary" }}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              loc.isPrimary
                                ? "Set another location as primary before removing this one"
                                : "Remove Location"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setRemoveConfirm(loc)}
                                disabled={saving || loc.isPrimary}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  {empLocations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No locations assigned
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Add Location</InputLabel>
                <Select
                  value={addLocationId}
                  label="Add Location"
                  onChange={(e) => setAddLocationId(e.target.value)}
                >
                  {availableLocations.map((l) => (
                    <MenuItem key={l.locationId} value={l.locationId}>
                      {l.medicalCenter}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAssignLocation}
                disabled={!addLocationId || saving}
              >
                Assign
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <ConfirmDialog
        open={Boolean(removeConfirm)}
        title="Remove Location"
        message={`Remove ${removeConfirm?.medicalCenter} from this employee?`}
        confirmLabel="Remove"
        onConfirm={handleRemoveLocation}
        onCancel={() => setRemoveConfirm(null)}
        loading={saving}
      />

      <ConfirmDialog
        open={Boolean(userAction)}
        title={userAction ? userActionTitles[userAction] : ""}
        message={userAction ? userActionMessages[userAction] : ""}
        confirmLabel={userAction ? userActionLabels[userAction] : ""}
        confirmColor={
          userAction === "reactivate" || userAction === "unlock"
            ? "success"
            : "error"
        }
        onConfirm={handleUserAction}
        onCancel={() => setUserAction(null)}
        loading={saving}
      />

      <MfaManageDialog
        open={mfaOpen}
        user={
          employee
            ? {
                ...employee,
                mfaEnabled: header?.mfaEnabled,
                mfaFrequencyDays: header?.mfaFrequencyDays,
              }
            : null
        }
        onClose={() => {
          setMfaOpen(false);
          loadData();
        }}
        onSuccess={(msg) => setSuccess(msg)}
        onError={(msg) => setError(msg)}
      />
    </Dialog>
  );
};

export default EmployeeDetailDialog;
